/**
 * "Done today" tasks on the Admin "Today" page (design 3a, Phase 7) — the lower bound of the
 * tasks/subscribeCompletedTasksToday Firestore query (`completedAt >= bound`).
 *
 * Plan: C:\Users\nilay\.claude\plans\eager-painting-hejlsberg.md.
 *
 * ⚠ WHY NOT simply "start of the office day": tasks/updateTask stamps a task it completes with
 * `completedAt = moment.tz(officeTimezone).startOf('day')`. Until 2026-10-06 `officeTimezone` came
 * from a store module that does not exist, so EVERY stamp was the start of the day in
 * 'America/New_York'. The zone is fixed now (src/utils/tasks/officeTimezone.js), but completions
 * stamped before the fix keep their New York stamp — so the caller passes stampZone = New York. For an office WEST of New York, "start of the
 * office day" is LATER than that stamp (00:00 Pacific = 03:00 Eastern), so a bound of the office
 * midnight would hide EVERY task the office finishes today.
 *
 * So the bound is the EARLIER of
 *   (a) the start of today in the office zone (right if the store ever stamps in the office zone,
 *       and for completions that carry a real time — see subscribeCompletedTasksToday), and
 *   (b) the start of the office's calendar date in the zone the store stamps in.
 * Known, accepted slack (the stamp cannot tell these apart): an office west of New York also sees
 * tasks finished late last evening (after 00:00 New York — 21:00 Pacific); an office east of New
 * York does not see a task finished between its midnight and New York's midnight (00:00-06:00 in
 * South Africa).
 *
 * Kept IMPORT-FREE (node-tests execute it verbatim): moment-timezone is passed in.
 */

/** The zone every completion was stamped in before the 2026-10-06 fix (and the store's fallback). */
export const TASK_STAMP_DEFAULT_ZONE = 'America/New_York';

function validZone(tz, zone, fallback) {
  return typeof zone === 'string' && zone && tz.tz.zone(zone) ? zone : fallback;
}

/**
 * @param {Object} o
 * @param {Function} o.moment     moment-timezone (injected: this file stays import-free)
 * @param {string}   o.officeZone the site's GENERAL_TIMEZONE (invalid / missing ⇒ New York)
 * @param {string}   [o.stampZone] the zone tasks/updateTask stamps completedAt in (default New York)
 * @param {Date|number} o.now     "now" — passed in (this file never reads the clock)
 * @returns {Date}
 */
export function completedTodayBound({ moment: tz, officeZone, stampZone, now } = {}) {
  const office = validZone(tz, officeZone, TASK_STAMP_DEFAULT_ZONE);
  const stamp = validZone(tz, stampZone, TASK_STAMP_DEFAULT_ZONE);
  const officeStart = tz.tz(now, office).startOf('day');
  const stampStart = tz.tz(officeStart.format('YYYY-MM-DD'), 'YYYY-MM-DD', stamp).startOf('day');
  return (officeStart.valueOf() <= stampStart.valueOf() ? officeStart : stampStart).toDate();
}
